/**
 * `/tina/health` — the systemic-failure surface.
 *
 * `/insights/errors` and `/insights/stages` have been built in Tina and called
 * from nowhere: `tina-metrics` was a deliberately route-less resource, and the
 * repository methods `getErrors`/`getStages` had no caller. The cost of that was
 * measured on 2026-08-26, when 141 events were silently poisoned over three days
 * and the incident was found by hand-querying Postgres, because nothing in IMS
 * shows failures grouped by *signature*.
 *
 * Two questions, two sections:
 *
 * 1. **What is failing, and is it new?** — `/insights/errors` clustered by
 *    `(stage, error_type)` with a trend verdict. Regressions sort above volume.
 * 2. **Which stage is unhealthy or slow?** — `/insights/stages` rolled up per
 *    stage, with the success rate and a latency approximation.
 *
 * Neither endpoint is class-filtered, so unlike `/tina/home`'s KPI strip — which
 * reads the scrape-only `/insights/overview` — this page sees enrichment and
 * scraping together. The class filter is a deliberate choice here, not a
 * server-side default.
 *
 * Failures a later retry fixed are hidden by default (Tina migration `0077`
 * marks them): they were 72% of all errors in the week to 2026-09-23 and buried
 * the ones that stayed broken. "Show self-healed" puts them back.
 */
import { type ReactNode, useMemo, useState } from "react";

import { useNavigation } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  Bug,
  CheckCircle2,
  Gauge,
  HeartPulse,
  RotateCcw,
  Sparkles,
  TrendingUp,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Toggle } from "@/components/ui/toggle";
import { cn } from "@/lib/utils";
import {
  type ErrorCluster,
  type ErrorTrend,
  aggregateErrorClusters,
  countRecoveredErrors,
  isRegression,
} from "@/pages/tina/health/lib/error-clusters";
import {
  isSiteStep,
  splitSiteErrors,
} from "@/pages/tina/health/lib/site-errors";
import {
  NON_OK_STATUSES,
  type StageClass,
  type StageHealthRow,
  aggregateStageHealth,
  formatDominantOther,
  formatMs,
  formatRate,
} from "@/pages/tina/health/lib/stage-health";
import { useMetricsRepository } from "@/pages/tina/hooks/use-metrics-repository";
import {
  PERIOD_LABEL,
  PeriodChips,
  type PeriodKey,
  usePeriodRange,
} from "@/pages/tina/lib/period";

/**
 * Failure rate above which a stage is called out, and above which it is called
 * broken. These replace a pair of thresholds on the old `successRate`
 * (`< 0.99` warn, `< 0.9` bad) that were calibrated against a metric counting
 * `inherited`/`skipped`/`duplicate`/`hitl`/`deferred` as failures — on prod's
 * seven days to 2026-08-28 that tripped **16 of 22 stages**, so the "unhealthy
 * stages" card was permanently red and carried no information.
 *
 * Against real failure rates over the same window these select `early_dup`
 * (6.80%) as bad, and `artist_disambiguation` (2.33%), `discover` (2.22%),
 * `late_dup` (1.82%), `fetch_check` (1.66%) and `artist_resolution` (1.05%) as
 * warnings — 1 red and 5 amber out of 22, which is a detector rather than a
 * mood light.
 */
const STAGE_WARN_FAIL_RATE = 0.01;
/** …and above which it is called broken. */
const STAGE_BAD_FAIL_RATE = 0.05;

type ClassFilter = "all" | StageClass;

const CLASS_FILTER_LABEL: Record<ClassFilter, string> = {
  all: "All stages",
  scrape: "Scrape",
  enrichment: "Enrichment",
};

const TREND_STYLE: Record<ErrorTrend, { label: string; className: string }> = {
  new: {
    label: "New",
    className:
      "bg-red-500/10 text-red-700 ring-1 ring-red-500/20 dark:text-red-300",
  },
  spiking: {
    label: "Spiking",
    className:
      "bg-orange-500/10 text-orange-700 ring-1 ring-orange-500/20 dark:text-orange-300",
  },
  steady: {
    label: "Steady",
    className:
      "bg-zinc-500/10 text-zinc-700 ring-1 ring-zinc-500/20 dark:text-zinc-300",
  },
  fading: {
    label: "Fading",
    className:
      "bg-sky-500/10 text-sky-600 ring-1 ring-sky-500/20 dark:text-sky-300",
  },
};

function TrendBadge({ trend }: { trend: ErrorTrend }) {
  const style = TREND_STYLE[trend];
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium",
        style.className,
      )}
    >
      {style.label}
    </span>
  );
}

function StageClassChip({ stageClass }: { stageClass: StageClass }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ring-1",
        stageClass === "enrichment"
          ? "bg-indigo-500/10 text-indigo-700 ring-indigo-500/20 dark:text-indigo-300"
          : "bg-zinc-500/10 text-zinc-600 ring-zinc-500/20 dark:text-zinc-400",
      )}
    >
      {stageClass === "enrichment" ? "enrich" : "scrape"}
    </span>
  );
}

/**
 * Per-day counts as bars, scaled to the cluster's own maximum.
 *
 * Deliberately not a recharts chart: there is one of these per table row, the
 * shape is all that is being read, and the numbers are already in the row.
 */
function SparkBar({ daily }: { daily: ErrorCluster["daily"] }) {
  const max = daily.reduce((acc, d) => Math.max(acc, d.count), 0) || 1;
  return (
    <div
      className="flex h-6 items-end gap-px"
      aria-label={`${daily.length} days, peak ${max}`}
    >
      {daily.map((d) => (
        <div
          key={d.day}
          title={`${d.day}: ${d.count.toLocaleString()}`}
          className="w-1.5 rounded-sm bg-muted-foreground/50"
          style={{ height: `${Math.max(8, (d.count / max) * 100)}%` }}
        />
      ))}
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  icon,
  tone,
}: {
  label: string;
  value: string;
  hint: string;
  icon: ReactNode;
  tone: "sky" | "red" | "orange" | "emerald";
}) {
  const chip = {
    sky: "bg-sky-500/10 text-sky-700 ring-sky-500/20 dark:text-sky-300",
    red: "bg-red-500/10 text-red-700 ring-red-500/20 dark:text-red-300",
    orange:
      "bg-orange-500/10 text-orange-700 ring-orange-500/20 dark:text-orange-300",
    emerald:
      "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20 dark:text-emerald-300",
  }[tone];
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <span
            className={cn(
              "inline-flex h-8 w-8 items-center justify-center rounded-md ring-1",
              chip,
            )}
          >
            {icon}
          </span>
          <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            {label}
          </span>
        </div>
        <div className="mt-2 text-3xl font-bold tabular-nums leading-none">
          {value}
        </div>
        <div className="mt-1.5 text-[11px] text-muted-foreground">{hint}</div>
      </CardContent>
    </Card>
  );
}

/** Colour for a FAILURE rate — higher is worse, the opposite of the old one. */
function failTone(rate: number | null): string {
  if (rate == null) return "text-muted-foreground";
  if (rate > STAGE_BAD_FAIL_RATE) return "text-red-600 dark:text-red-400";
  if (rate > STAGE_WARN_FAIL_RATE) return "text-amber-600 dark:text-amber-400";
  return "text-emerald-600 dark:text-emerald-400";
}

function ErrorClustersTable({
  clusters,
  hiddenRecovered,
  eventHref,
  outages,
}: {
  clusters: ErrorCluster[];
  /** Self-healed errors left out of `clusters`, for the empty state. */
  hiddenRecovered: number;
  eventHref: (id: string) => string;
  /** Site-step (discover/fetch) errors that hit `OUTAGE_MIN_SITES` on one day. */
  outages: Map<string, { day: string; siteCount: number }>;
}) {
  if (clusters.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-1 py-10 text-sm text-muted-foreground">
        <CheckCircle2 className="h-5 w-5 opacity-60" />
        {hiddenRecovered > 0
          ? `No open errors in this window · ${hiddenRecovered.toLocaleString()} fixed on retry are hidden`
          : "No stage errors in this window"}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[84px]">Trend</TableHead>
            <TableHead>Error type</TableHead>
            <TableHead>Stage</TableHead>
            <TableHead className="text-right">Total</TableHead>
            <TableHead className="text-right">Latest day</TableHead>
            <TableHead className="w-[120px]">Shape</TableHead>
            <TableHead>Seen</TableHead>
            <TableHead>Sample events</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {clusters.map((c) => (
            <TableRow
              key={c.key}
              className={cn(isRegression(c) && "bg-red-500/[0.03]")}
            >
              <TableCell>
                <TrendBadge trend={c.trend} />
              </TableCell>
              <TableCell className="max-w-[280px]">
                <span className="font-mono text-xs break-all">
                  {c.errorType}
                </span>
                {outages.get(c.key) && (
                  <Badge variant="destructive" className="ml-2 text-[10px]">
                    {`outage · ${outages.get(c.key)!.siteCount} sites on ${outages.get(c.key)!.day}`}
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-xs">{c.stage}</span>
                  <StageClassChip stageClass={c.stageClass} />
                </div>
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums">
                {c.total.toLocaleString()}
                {c.recoveredTotal > 0 && (
                  <div className="text-[10px] text-muted-foreground">
                    {c.recoveredTotal.toLocaleString()} fixed on retry
                  </div>
                )}
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-muted-foreground">
                {c.latestCount.toLocaleString()}
              </TableCell>
              <TableCell>
                <SparkBar daily={c.daily} />
              </TableCell>
              <TableCell className="whitespace-nowrap text-[11px] text-muted-foreground">
                {c.firstDay === c.lastDay
                  ? dayjs(c.firstDay).format("MMM D")
                  : `${dayjs(c.firstDay).format("MMM D")} → ${dayjs(
                      c.lastDay,
                    ).format("MMM D")}`}
                <span className="ml-1">
                  ({c.days} day{c.days === 1 ? "" : "s"})
                </span>
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap items-center gap-1">
                  {c.sampleEventIds.map((id) => (
                    // A real link in a new tab, so triaging several samples
                    // never loses this page's filters and scroll position.
                    <Button
                      key={id}
                      asChild
                      variant="outline"
                      size="sm"
                      className="h-6 gap-1 px-1.5 font-mono text-[10px]"
                    >
                      <a href={eventHref(id)} target="_blank" rel="noreferrer">
                        {id.slice(0, 8)}
                        <ArrowUpRight className="h-3 w-3" />
                      </a>
                    </Button>
                  ))}
                  {c.sampleTotal > c.sampleEventIds.length && (
                    <span className="text-[10px] text-muted-foreground">
                      +
                      {(
                        c.sampleTotal - c.sampleEventIds.length
                      ).toLocaleString()}{" "}
                      more
                    </span>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function StageHealthTable({ rows }: { rows: StageHealthRow[] }) {
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-1 py-10 text-sm text-muted-foreground">
        <Activity className="h-5 w-5 opacity-60" />
        No stage executions in this window
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Stage</TableHead>
            <TableHead className="text-right">Executions</TableHead>
            <TableHead className="text-right">Failed</TableHead>
            <TableHead className="w-[200px]">Failure rate</TableHead>
            <TableHead
              className="text-right"
              title="Execution-weighted mean of the per-(day, prompt version) p50s. Percentiles do not recombine, so this is a rough approximation, not a true percentile — measured against prod it has been off by -63% to +862%. Do not rank stages by it."
            >
              ≈ p50
            </TableHead>
            <TableHead
              className="text-right"
              title="Execution-weighted mean of the per-(day, prompt version) p95s. Same caveat as p50: a rough approximation, not a true percentile, and not a reliable ranking."
            >
              ≈ p95
            </TableHead>
            <TableHead>Worst day</TableHead>
            <TableHead className="text-right">Prompts</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.stage}>
              <TableCell>
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-xs">{r.stage}</span>
                  <StageClassChip stageClass={r.stageClass} />
                </div>
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums">
                {r.executions.toLocaleString()}
              </TableCell>
              <TableCell
                className={cn(
                  "text-right font-mono tabular-nums",
                  r.failCount > 0 && "text-red-600 dark:text-red-400",
                )}
              >
                {r.failCount.toLocaleString()}
              </TableCell>
              <TableCell>
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-20 overflow-hidden rounded-full bg-muted">
                      {/*
                        The bar shows the FAILURE share, so a healthy stage is an
                        empty track rather than a full green one. A non-zero but
                        tiny rate still gets a visible sliver.
                      */}
                      <div
                        className={cn(
                          "h-full rounded-full",
                          r.failRate != null && r.failRate > STAGE_BAD_FAIL_RATE
                            ? "bg-red-500"
                            : r.failRate != null &&
                                r.failRate > STAGE_WARN_FAIL_RATE
                              ? "bg-amber-500"
                              : "bg-emerald-500",
                        )}
                        style={{
                          width: r.failCount
                            ? `${Math.max(4, (r.failRate ?? 0) * 100)}%`
                            : "0%",
                        }}
                      />
                    </div>
                    <span
                      className={cn(
                        "font-mono text-xs tabular-nums",
                        failTone(r.failRate),
                      )}
                    >
                      {formatRate(r.failRate)}
                    </span>
                  </div>
                  {/*
                    Why the stage is not `ok`, so nobody reads a stage that
                    inherits or dedupes most of its work as a broken one.
                  */}
                  <span
                    className="text-[11px] text-muted-foreground"
                    title={
                      r.statusMix
                        ? NON_OK_STATUSES.filter((k) => r.statusMix?.[k])
                            .map(
                              (k) =>
                                `${r.statusMix?.[k].toLocaleString()} ${k}`,
                            )
                            .join(" · ")
                        : undefined
                    }
                  >
                    {formatDominantOther(r) || `${formatRate(r.okRate)} ok`}
                    {r.recoveredCount > 0 &&
                      ` · ${r.recoveredCount.toLocaleString()} fixed on retry`}
                  </span>
                </div>
              </TableCell>
              <TableCell className="text-right font-mono text-xs tabular-nums text-muted-foreground">
                {formatMs(r.p50Ms)}
              </TableCell>
              <TableCell className="text-right font-mono text-xs tabular-nums text-muted-foreground">
                {formatMs(r.p95Ms)}
              </TableCell>
              <TableCell className="whitespace-nowrap text-[11px] text-muted-foreground">
                {r.worstDay
                  ? `${dayjs(r.worstDay.day).format("MMM D")} · ${formatRate(
                      r.worstDay.failRate,
                    )} failed`
                  : "—"}
              </TableCell>
              <TableCell className="text-right font-mono text-xs tabular-nums text-muted-foreground">
                {r.promptVersions || "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export const TinaHealth = () => {
  const [period, setPeriod] = useState<PeriodKey>("24h");
  const [classFilter, setClassFilter] = useState<ClassFilter>("all");
  const [search, setSearch] = useState("");
  const [regressionsOnly, setRegressionsOnly] = useState(false);
  const [problemStagesOnly, setProblemStagesOnly] = useState(false);
  const [showRecovered, setShowRecovered] = useState(false);
  const { showUrl } = useNavigation();
  const repo = useMetricsRepository();
  const range = usePeriodRange(period);

  const errorsQ = useQuery({
    queryKey: ["tina", "metrics", "errors", range.since, range.until],
    queryFn: () => repo.getErrors(range.since, range.until),
  });
  const stagesQ = useQuery({
    queryKey: ["tina", "metrics", "stages", range.since, range.until],
    queryFn: () => repo.getStages(range.since, range.until),
  });

  const split = useMemo(
    () =>
      splitSiteErrors(errorsQ.data ?? [], { includeRecovered: showRecovered }),
    [errorsQ.data, showRecovered],
  );
  const clusters = useMemo(
    () =>
      aggregateErrorClusters(split.pipelineRows, {
        includeRecovered: showRecovered,
      }),
    [split.pipelineRows, showRecovered],
  );
  const stages = useMemo(
    () =>
      aggregateStageHealth(stagesQ.data ?? [], {
        includeRecovered: showRecovered,
      }),
    [stagesQ.data, showRecovered],
  );
  const hiddenRecovered = useMemo(
    () => (showRecovered ? 0 : countRecoveredErrors(errorsQ.data ?? [])),
    [errorsQ.data, showRecovered],
  );

  const needle = search.trim().toLowerCase();
  const visibleClusters = useMemo(
    () =>
      clusters.filter((c) => {
        if (classFilter !== "all" && c.stageClass !== classFilter) return false;
        if (regressionsOnly && !isRegression(c)) return false;
        if (
          needle &&
          !c.errorType.toLowerCase().includes(needle) &&
          !c.stage.toLowerCase().includes(needle)
        )
          return false;
        return true;
      }),
    [clusters, classFilter, regressionsOnly, needle],
  );
  const visibleStages = useMemo(
    () =>
      stages.filter((r) => {
        if (classFilter !== "all" && r.stageClass !== classFilter) return false;
        if (
          problemStagesOnly &&
          (r.failRate == null || r.failRate <= STAGE_WARN_FAIL_RATE)
        )
          return false;
        if (needle && !r.stage.toLowerCase().includes(needle)) return false;
        return true;
      }),
    [stages, classFilter, problemStagesOnly, needle],
  );
  const pipelineStages = visibleStages.filter((r) => !isSiteStep(r.stage));
  const siteStepStages = visibleStages.filter((r) => isSiteStep(r.stage));

  const regressions = clusters.filter(isRegression);
  const totalErrors = clusters.reduce((acc, c) => acc + c.total, 0);
  // Stages that are actually FAILING. The old predicate was
  // `successRate < 0.99`, which on prod flagged 16 of 22 stages — including
  // `past_gate` and `movie_materialization`, both at a 0.00% failure rate.
  // `discover`/`fetch` are excluded: their failures are almost always one
  // broken site, not a systemic problem, and they have their own dimmed group
  // below rather than driving this card red.
  const unhealthyStages = stages.filter(
    (r) =>
      !isSiteStep(r.stage) &&
      r.failRate != null &&
      r.failRate > STAGE_WARN_FAIL_RATE,
  );
  const slowest = stages.reduce<StageHealthRow | null>(
    (acc, r) =>
      r.p95Ms != null && (!acc || r.p95Ms > (acc.p95Ms ?? 0)) ? r : acc,
    null,
  );

  const loading = errorsQ.isLoading || stagesQ.isLoading;
  // Both endpoints are new callers of routes that have never been exercised from
  // IMS. A 404 (an orchestrator deploy older than the routes) or a 401 must not
  // render as "no errors in this window" — that is the exact failure mode this
  // page exists to prevent.
  const failed = [
    errorsQ.isError ? "errors" : null,
    stagesQ.isError ? "stages" : null,
  ].filter(Boolean);

  return (
    <div className="flex flex-col gap-4 p-6 max-w-[1400px] mx-auto w-full">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border bg-background/60 px-2.5 py-0.5 text-[11px] font-medium">
            <HeartPulse className="h-3 w-3" />
            Tina · pipeline health
          </div>
          <h1 className="mt-2 text-2xl font-bold tracking-tight">
            Errors &amp; stage health
          </h1>
          <p className="text-sm text-muted-foreground">
            Every failure Tina recorded, clustered by signature — scraping and
            enrichment together. Unlike the overview KPIs, nothing here is
            filtered to the scrape class. Failures a retry fixed are hidden
            unless you turn on “Show self-healed”. Single-site discover and
            fetch errors are on Site health; they show here only when one
            error hits 10 or more sites in a day.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-[0.1em] text-muted-foreground">
            Window
          </span>
          <PeriodChips value={period} onChange={setPeriod} />
        </div>
      </div>

      {failed.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
          <div>
            <div className="font-medium text-red-700 dark:text-red-300">
              Could not load /insights/{failed.join(" and /insights/")}
            </div>
            <div className="text-[12px] text-muted-foreground">
              What is shown below is incomplete. Check the orchestrator is
              reachable and recent enough to serve these routes.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[110px] w-full" />
          ))
        ) : (
          <>
            <Stat
              label="Regressions"
              value={regressions.length.toLocaleString()}
              hint="new or spiking signatures"
              icon={<TrendingUp className="h-4 w-4" />}
              tone={regressions.length > 0 ? "red" : "emerald"}
            />
            <Stat
              label="Failures"
              value={totalErrors.toLocaleString()}
              hint={`${clusters.length} distinct signatures · last ${PERIOD_LABEL[period]}${
                hiddenRecovered > 0
                  ? ` · ${hiddenRecovered.toLocaleString()} fixed on retry hidden`
                  : ""
              }`}
              icon={<Bug className="h-4 w-4" />}
              tone="orange"
            />
            <Stat
              label="Unhealthy stages"
              value={unhealthyStages.length.toLocaleString()}
              hint={`above ${(STAGE_WARN_FAIL_RATE * 100).toFixed(0)}% failures · of ${stages.length} running`}
              icon={<AlertTriangle className="h-4 w-4" />}
              tone={unhealthyStages.length > 0 ? "red" : "emerald"}
            />
            <Stat
              label="Slowest stage"
              value={slowest ? formatMs(slowest.p95Ms) : "—"}
              hint={
                slowest ? `${slowest.stage} · ≈ p95` : "no latency recorded"
              }
              icon={<Gauge className="h-4 w-4" />}
              tone="sky"
            />
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center rounded-md border bg-background p-0.5">
          {(["all", "scrape", "enrichment"] as ClassFilter[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setClassFilter(k)}
              className={cn(
                "px-2.5 py-1 text-xs font-medium rounded-sm transition-colors",
                classFilter === k
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted",
              )}
            >
              {CLASS_FILTER_LABEL[k]}
            </button>
          ))}
        </div>
        <Toggle
          size="sm"
          variant="outline"
          pressed={regressionsOnly}
          onPressedChange={setRegressionsOnly}
          className="h-8 gap-1.5 px-2 text-xs"
        >
          <Sparkles className="h-3 w-3" />
          Regressions only
        </Toggle>
        <Toggle
          size="sm"
          variant="outline"
          pressed={problemStagesOnly}
          onPressedChange={setProblemStagesOnly}
          className="h-8 gap-1.5 px-2 text-xs"
        >
          <AlertTriangle className="h-3 w-3" />
          Failing stages only
        </Toggle>
        <Toggle
          size="sm"
          variant="outline"
          pressed={showRecovered}
          onPressedChange={setShowRecovered}
          className="h-8 gap-1.5 px-2 text-xs"
          title="Also count the failures a later retry of the same stage fixed"
        >
          <RotateCcw className="h-3 w-3" />
          Show self-healed
        </Toggle>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by stage or error type…"
          className="h-8 w-[260px] text-xs"
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Bug className="h-4 w-4 text-muted-foreground" />
            Error clusters
            {regressions.length > 0 && (
              <Badge variant="destructive" className="text-[10px]">
                {regressions.length} need a look
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            One row per (stage, error type) over the last {PERIOD_LABEL[period]}
            . New and spiking signatures sort above volume — a brand-new error
            type with 141 occurrences matters more than a known one with
            141,000. An error a later retry of the same stage fixed is not
            counted unless “Show self-healed” is on.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3 py-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : (
            <>
              {split.hiddenSiteErrors > 0 && (
                <p className="mb-2 text-[12px] text-muted-foreground">
                  {`${split.hiddenSiteErrors.toLocaleString()} site errors hidden (discover/fetch on single sites) → `}
                  {/* A plain anchor: this page renders without a router in its tests, like its sample-event links. */}
                  <a href="/tina/site-health" className="underline">
                    Site health
                  </a>
                </p>
              )}
              <ErrorClustersTable
                clusters={visibleClusters}
                hiddenRecovered={hiddenRecovered}
                eventHref={(id) => showUrl("tina-items", id)}
                outages={split.outages}
              />
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4 text-muted-foreground" />
            Stage health
          </CardTitle>
          <CardDescription>
            Per-stage failure rate and latency, rolled up across days and prompt
            versions. Only <code className="text-[11px]">failed</code> and{" "}
            <code className="text-[11px]">failed_retryable</code> count as
            failures, and a failure a later retry fixed does not count unless
            “Show self-healed” is on — the second line of each row says why a
            stage is not <code className="text-[11px]">ok</code>, which for most
            stages is inheritance, dedup or a deliberate skip rather than a
            problem. The p50/p95 columns are execution-weighted means of the
            per-(day, prompt version) percentiles: percentiles do not recombine,
            so treat them as rough indicators only — measured against prod they
            have been off by −63% to +862%, and they do not rank stages
            reliably.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3 py-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : (
            <>
              <StageHealthTable rows={pipelineStages} />
              {siteStepStages.length > 0 && (
                <div className="mt-4 opacity-60">
                  <div className="mb-1 text-[11px] font-medium text-muted-foreground">
                    Site steps — failures here are mostly single sites; see
                    Site health
                  </div>
                  <StageHealthTable rows={siteStepStages} />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
